import { HashtagIcon } from "@heroicons/react/24/outline";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";

import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  CaretSortIcon,
  CheckIcon,
  ChevronRightIcon,
  Pencil2Icon,
} from "@radix-ui/react-icons";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetSettings } from "src/api/General/settings";
import { useGetUsers } from "src/api/General/users";
import {
  useGetSlackChannels,
  useGetSlackIntegrationStatus,
} from "src/api/Integrations/slack";
import {
  SpaceData,
  SpaceMembershipData,
  useAddUserToSpace,
  useDeleteSpace,
  useGetSpace,
  useGetSpaceMemberships,
  useRemoveUserFromSpace,
  useUpdateSpace,
  useUpdateSpaceMembership,
} from "src/api/Spaces/spaces";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import InputHeading from "src/components/ui/Headings/InputHeading";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { SpaceNameAndSymbol } from "src/pages/spaces/spaces";

type Props = {};

export default function SpaceNotificationSettings({}: Props) {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const { data: settings } = useGetSettings();
  const [deletingOpen, setDeletingOpen] = useState(false);
  const deleteSpaceMutation = useDeleteSpace();
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  useEffect(() => {
    if (deleteSpaceMutation.isSuccess) {
      setDeletingOpen(false);
    }
  }, [deleteSpaceMutation.isSuccess]);
  const [open, setOpen] = useState(false);
  const addUserToSpaceMutation = useAddUserToSpace();
  const { data: memberships } = useGetSpaceMemberships(String(spaceId));
  const { users } = useGetUsers();
  const addOptions = useMemo(() => {
    if (!users || !memberships) return [];
    return users.filter(
      (user) =>
        !memberships?.some((m) => m.user._id.toString() === user._id.toString())
    );
  }, [memberships, users]);
  const [requireInternalProjectUpdates, setRequireInternalProjectUpdates] =
    useState(false);

  return (
    <div className="flex h-screen flex-col bg-neutral-50/50">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings/spaces/${spaceId}`}>
            {space && (
              <SpaceNameAndSymbol name={space.name} color={space.color} />
            )}
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Notifications"}</div>
        </div>
      </div>
      <div className="mx-auto w-full max-w-2xl pt-20">
        <div className="space-y-1 border-b border-gray-200/70 pb-6">
          <div className="text-xl font-medium">Notifications</div>
          {/* <div className="text-sm font-light text-gray-600">
            Edit the default settings for projects in this space
          </div> */}
        </div>
        <Notifications />
      </div>
    </div>
  );
}

function Notifications() {
  const { spaceId } = useParams();
  const { data: space } = useGetSpace(String(spaceId));
  const updateSpaceMutation = useUpdateSpace();

  return (
    <div className="space-y-3.5 pt-8">
      <div className="space-y-0">
        <div className="font-medium text-gray-800">
          Deafult Email Notifications
        </div>
        <div className="text-sm font-light text-gray-500">
          Set the default email notifications for new projects in the space
        </div>
      </div>

      <div className="space-y-1">
        <div className="space-y-5 rounded-md border border-gray-200/70 bg-white p-8 pt-7 shadow-sm">
          
          <div className="flex items-center justify-between gap-10">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">
                Task Overdue Email
              </div>
              <div className="max-w-prose text-[13px] font-light text-gray-500">
              Send an email to the task assignee when a task goes overdue
              </div>
            </div>
            <Switch
              checked={
                space?.settings.emailNotifications.overdueEmail
              }
              onCheckedChange={(value) => {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    emailNotifications: {
                      overdueEmail: value,
                    },
                  },
                });
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-10">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">
                Task Activation Email
              </div>
              <div className="max-w-prose text-[13px] font-light text-gray-500">
              Send an email to the task assignee when a new task is activated for them.
              </div>
            </div>
            <Switch
              checked={
                space?.settings.emailNotifications.dependentTaskActivationEmail
              }
              onCheckedChange={(value) => {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    emailNotifications: {
                      dependentTaskActivationEmail: value,
                    },
                  },
                });
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-10">
            <div className="space-y-0">
              <div className="text-sm font-medium text-gray-800">
                Weekly Update Email
              </div>
              <div className="max-w-prose text-[13px] font-light text-gray-500">
                Send an email to all external members every Monday that includes
                the past week's achievements and the upcoming week's goals.
              </div>
            </div>
            <Switch
              checked={
                space?.settings.emailNotifications.weeklyUpdateEmail
              }
              onCheckedChange={(value) => {
                updateSpaceMutation.mutate({
                  _id: String(spaceId),
                  settings: {
                    emailNotifications: {
                      weeklyUpdateEmail: value,
                    },
                  },
                });
              }}
            />
          </div>
        </div>
        </div>
    </div>
  );
}

